
import { supabase } from '@/lib/supabase';
import { toast } from '@/hooks/use-toast';
import { formatQRCode } from '@/utils/qrCodeUtils';

// Helper to remove accents/diacritics from text consistently
const normalizeText = (text: string): string => {
  if (!text) return '';
  return text.normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, 'd').replace(/Đ/g, 'D'); // Special handling for Vietnamese characters
};

// Enhanced QR code URL generation with optimal settings for email
export const generateQRImageURL = (attendeeData: any): string | null => {
  if (!attendeeData) return null;
  
  try {
    // Get the code and ensure it's properly formatted
    const rawCode = attendeeData.code || '';
    const formattedCode = formatQRCode(rawCode);
    
    // Log the formatted code for debugging
    console.log(`Generating QR for email with code: ${formattedCode}`);
    
    // For consistent scanning, use only the formatted code
    const encodedQrData = encodeURIComponent(formattedCode);
    
    // Optimize QR settings for email display and scanning reliability
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodedQrData}&margin=4&ecc=H&qzone=2&format=png`;
    return qrCodeUrl;
  } catch (error) {
    console.error("Error generating QR URL:", error);
    return null;
  }
};

// Enhanced email sending with improved QR code handling
export const sendConfirmationEmail = async (data: any) => {
  try {
    // Format the code properly
    const formattedCode = formatQRCode(data.code || '');
    
    // Get the t-shirt size with proper formatting
    const tShirtSize = data.t_shirt_size || 'Nam_L';
    
    // Format size for display
    let displaySize;
    if (tShirtSize.startsWith('Nam_')) {
      displaySize = `${tShirtSize.substring(4)} (Nam)`;
    } else if (tShirtSize.startsWith('Nu_')) {
      displaySize = `${tShirtSize.substring(3)} (Nữ)`;
    } else {
      // Fallback
      displaySize = tShirtSize;
    }
    
    // Call our Supabase Edge Function to send the email
    const { data: response, error } = await supabase.functions.invoke('send-confirmation', {
      body: {
        name: normalizeText(data.name || ''),
        email: data.email,
        phone: data.phone || '',
        class: normalizeText(data.class || ''),
        code: formattedCode,
        contribution: data.contribution,
        // Use the formatted code for QR generation
        qrData: formattedCode,
        t_shirt_size: tShirtSize,
        t_shirt_display: displaySize
      }
    });
    
    if (error) {
      throw new Error(error.message);
    }
    
    if (response && !response.success) {
      throw new Error(response.error || 'Unknown error occurred');
    }
    
    toast({
      title: "Email xác nhận đã được gửi",
      description: "Vui lòng kiểm tra hòm thư email của bạn.",
    });
    
    return { success: true, response };
  } catch (error) {
    console.error("Error sending confirmation email:", error);
    
    const errorMessage = error instanceof Error 
      ? error.message 
      : 'Không thể gửi email xác nhận. Vui lòng lưu lại mã QR và thông tin đăng ký.';
    
    toast({
      title: "Không thể gửi email xác nhận",
      description: "Vui lòng lưu lại mã QR để sử dụng tại sự kiện.",
      variant: "destructive"
    });
    
    return { success: false, error: errorMessage };
  }
};

// Enhanced QR code download for better quality
export const downloadQRCode = (qrImageURL: string, attendeeData: any) => {
  if (!qrImageURL) return;
  
  // Format the code properly
  const formattedCode = formatQRCode(attendeeData?.code || '');
  
  // Create a download link with better quality settings
  const highQualityUrl = qrImageURL.replace('size=300x300', 'size=500x500').replace('&margin=10', '&margin=4');
  
  const link = document.createElement('a');
  link.href = highQualityUrl;
  link.download = `ma-qr-${formattedCode || 'tham-du'}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  toast({
    title: "Tải mã QR thành công",
    description: "Vui lòng lưu giữ mã QR này để sử dụng tại sự kiện.",
  });
};
